import React, { FC }           from 'react';
import styled                  from 'styled-components';
import {
  Form,
  Field,
  useFormikContext,
}                              from 'formik';
import { IIntranetUserValues } from '@typings/manageIntranetUsers';
import {
  INTRANET_USER_STATUSES,
  INTRANET_USER_PERMANENT,
}                              from '@modules/manageIntranetUsers/utils/suggestions';
import {
  Button,
  Select,
  TextInput,
}                              from '@components/index';

const SectionWrapper = styled.div`
  margin-bottom: 30px;
`;

const LineWrapper = styled.div`
  display     : flex;
  width       : 600px;
  align-items : center;

  > span:first-child {
    flex-basis  : 200px;
    font-size   : 16px;
    text-align  : right;
    white-space : nowrap;
    color       : ${({ theme: { textColor } }): string => textColor.black};

    h2 {
      font-size     : 20px;
      margin-bottom : 10px;
    }
  }

  > div:last-child,
  > span:last-child {
    flex-basis  : 500px;
    margin-left : 15px;
    white-space : nowrap;
  }

  > span:last-child {
    font-size : 18px;
    color     : ${({ theme: { textColor } }): string => textColor.darkGray};
  }
`;

interface IProps {
  onCancel             : () => void;
  onDelete             : () => void;
  isNewUser            : boolean;
  isSuperAdmin         : boolean;
  isEditingOwnAccount  : boolean;
  isAuthRoleSuperAdmin : boolean;
  isUserDeleting       : boolean;
}

export const IntranetUserDetailsForm: FC<IProps> = ({
  onCancel,
  onDelete,
  isNewUser,
  isSuperAdmin,
  isEditingOwnAccount,
  isAuthRoleSuperAdmin,
  isUserDeleting,
}) => {
  const {
    dirty,
    errors,
    values,
    touched,
    isValid,
    isSubmitting,
  } = useFormikContext<IIntranetUserValues>();

  return (
    <Form>
      <SectionWrapper>
        <LineWrapper>
          <span>First Name:</span>
          <Field
            id         = "firstName"
            name       = "firstName"
            type       = "text"
            width      = "300px"
            stripChars = "[^a-zA-Z-.\s]"
            inputProps = {{ maxLength: 35 }}
            error      = {touched.firstName && !!errors.firstName}
            disabled   = {!(isEditingOwnAccount || (isAuthRoleSuperAdmin && !isSuperAdmin))}
            component  = {TextInput}
          />
        </LineWrapper>

        <LineWrapper>
          <span>Last Name:</span>
          <Field
            id         = "lastName"
            name       = "lastName"
            type       = "text"
            width      = "300px"
            stripChars = "[^a-zA-Z-.\s]"
            inputProps = {{ maxLength: 35 }}
            error      = {touched.lastName && !!errors.lastName}
            disabled   = {!(isEditingOwnAccount || (isAuthRoleSuperAdmin && !isSuperAdmin))}
            component  = {TextInput}
          />
        </LineWrapper>

        <LineWrapper>
          <span>Email Address:</span>
          <Field
            id         = "email"
            name       = "email"
            type       = "email"
            width      = "300px"
            inputProps = {{ maxLength: 50 }}
            error      = {touched.email && !!errors.email}
            component  = {TextInput}
            disabled   = {!isNewUser}
          />
        </LineWrapper>

        {(isNewUser || isEditingOwnAccount || (isAuthRoleSuperAdmin && !isSuperAdmin)) && (
          <>
            <LineWrapper>
              <span>Password:</span>
              <Field
                id         = "password"
                name       = "password"
                type       = "password"
                width      = "300px"
                inputProps = {{ maxLength: 32 }}
                error      = {touched.password && !!errors.password}
                component  = {TextInput}
              />
            </LineWrapper>

            <LineWrapper>
              <span>Confirm Password:</span>
              <Field
                id         = "confirmPassword"
                name       = "confirmPassword"
                type       = "password"
                width      = "300px"
                inputProps = {{ maxLength: 32 }}
                error      = {touched.confirmPassword && !!errors.confirmPassword}
                component  = {TextInput}
              />
            </LineWrapper>
          </>
        )}

        <LineWrapper>
          <span />
          <Field
            id        = "status"
            name      = "status"
            width     = "300px"
            align     = "center"
            disabled  = {isEditingOwnAccount || isSuperAdmin || !isAuthRoleSuperAdmin}
            component = {Select}
            value     = {isSuperAdmin ? 'permanentlyActive' : values.status}
            options   = {isSuperAdmin ? INTRANET_USER_PERMANENT : INTRANET_USER_STATUSES}
          />
        </LineWrapper>
      </SectionWrapper>

      <SectionWrapper>
        <LineWrapper>
          <span />
          <span>
            <Button
              variant  = "outlined"
              label    = "Cancel"
              width    = "300px"
              margin   = "0"
              onClick  = {onCancel}
            />
          </span>
        </LineWrapper>

        {(isNewUser || isEditingOwnAccount || (isAuthRoleSuperAdmin && !isSuperAdmin)) && (
          <LineWrapper>
            <span />
            <span>
              <Button
                type      = "submit"
                label     = "Save Changes"
                width     = "300px"
                margin    = "10px 0 0"
                isLoading = {isSubmitting}
                disabled  = {!isValid || !dirty || (isNewUser && !values.password) || isSubmitting}
              />
            </span>
          </LineWrapper>
        )}
      </SectionWrapper>

      {!isNewUser && !isSuperAdmin && isAuthRoleSuperAdmin && (
        <SectionWrapper>
          <LineWrapper>
            <span />
            <span>
              <Button
                label     = "Delete User"
                width     = "300px"
                margin    = "0"
                isLoading = {isUserDeleting}
                color     = "inherit"
                onClick   = {onDelete}
              />
            </span>
          </LineWrapper>
        </SectionWrapper>
      )}
    </Form>
  );
};
